import React from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { GUIDE_TEXT_MAX_WIDTH } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type { StudyHabitsWorkday } from '@backrs_ui/grow-ui/src/pages/StudyHabitsGuide/content/types';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

type WorkdayRulesSectionProps = Readonly<{
  content: StudyHabitsWorkday;
}>;

export const WorkdayRulesSection: React.FC<WorkdayRulesSectionProps> = ({ content }) => (
  <Box sx={containerSx}>
    <Typography variant="text-md-regular" sx={introSx}>
      {content.intro}
    </Typography>

    <Box sx={gridSx}>
      {content.rules.map((rule) => (
        <Box key={rule.id} sx={cardSx}>
          <Chip
            label={`${appStrings.modules.studyHabitsGuide.workday.ruleLabel} ${rule.number}`}
            variant="badge"
            size="sm"
            type="default"
            sx={chipSx}
          />
          <Box sx={cardBodySx}>
            <Typography variant="text-md-semibold" sx={titleSx}>
              {rule.title}
            </Typography>
            <Typography variant="text-sm-regular" sx={bodySx}>
              {rule.body}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-7xl'],
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'repeat(3, auto)',
    gridAutoFlow: 'column',
  },
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  p: backrs.spacing['spacing-3xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Brand/200']}`,
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Brand/700'],
  cursor: 'default',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/50'],
  },
});

const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
